import { useEffect, useMemo, useState } from "react";
import Lottie from "lottie-react";
import Button from "@/components/Button/Button";
import Modal from "@/components/Modal/Modal";
import { captalizeEachWord } from "@/lib/utils";
import { cancel } from "@/services/api/modal";
import styles from "./LogoutConfirmModal.module.scss";

export type ConfirmActionVariant = "logout" | "deleteAccount";

const VARIANT_CONFIG: Record<
  ConfirmActionVariant,
  {
    lottiePath: string;
    heading: string;
    message: string;
    confirmLabel: string;
  }
> = {
  logout: {
    lottiePath: "/lotiee/logout.json",
    heading: "Log Out",
    message: "Are you sure you want to log out?",
    confirmLabel: "Log Out",
  },
  deleteAccount: {
    lottiePath: "/lotiee/delete_account.json",
    heading: "Delete Account",
    message:
      "Are you sure you want to delete your account? This action cannot be undone.",
    confirmLabel: "Delete Account",
  },
};

interface LogoutConfirmModalProps {
  modalOpen: boolean;
  closeModal: (value: boolean) => void;
  onConfirm: () => void;
  variant?: ConfirmActionVariant;
  heading?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

const LogoutConfirmModal = ({
  modalOpen,
  closeModal,
  onConfirm,
  variant = "logout",
  heading,
  message,
  confirmLabel,
  cancelLabel,
}: LogoutConfirmModalProps) => {
  const [animationData, setAnimationData] = useState<object | null>(null);
  const config = VARIANT_CONFIG[variant];

  const resolvedHeading = heading ?? config.heading;
  const resolvedMessage = message ?? config.message;
  const resolvedConfirmLabel = confirmLabel ?? config.confirmLabel;
  const resolvedCancelLabel = cancelLabel ?? captalizeEachWord(cancel);

  useEffect(() => {
    if (!modalOpen) {
      return;
    }

    let isMounted = true;

    fetch(config.lottiePath)
      .then((response) => response.json())
      .then((data) => {
        if (isMounted) {
          setAnimationData(data);
        }
      })
      .catch(() => {
        if (isMounted) {
          setAnimationData(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [config.lottiePath, modalOpen]);

  const lottieKey = useMemo(
    () => `${variant}-${modalOpen ? "open" : "closed"}`,
    [modalOpen, variant],
  );

  const handleConfirm = () => {
    onConfirm();
    closeModal(false);
  };

  return (
    <Modal
      className={styles.modal}
      closeModal={closeModal}
      modalOpen={modalOpen}
    >
      <div className={styles.content}>
        {animationData ? (
          <Lottie
            animationData={animationData}
            className={styles.lottie}
            key={lottieKey}
            loop
          />
        ) : (
          <div aria-hidden className={styles.lottiePlaceholder} />
        )}
        <p className={styles.heading}>{resolvedHeading}</p>
        <p className={styles.message}>{resolvedMessage}</p>
      </div>

      <div className={styles.actions}>
        <Button className={styles.cancelButton} onClick={() => closeModal(false)}>
          {resolvedCancelLabel}
        </Button>
        <Button className={styles.confirmButton} onClick={handleConfirm}>
          {resolvedConfirmLabel}
        </Button>
      </div>
    </Modal>
  );
};

export default LogoutConfirmModal;
